import { useEffect, useMemo, useState } from "react";
import { Chart, ChartTypes, formatNumber } from "@uicore";
import { Card, IconButton, Stack, Typography } from "@altimateai/lego";
import { Bar, TooltipProps } from "recharts";
import dayjs from "dayjs";
import { useDatabricksCostOverview, useDatabricksPastTrends, useSearchParam } from "../hooks";
import { useAppState } from "@/modules/app/useAppContext";
import { IconInfoCircle } from "@tabler/icons-react";
import AggregateFilter from "../../../summary/components/AggregateFilter";
import FailureComponent from "../../../summary/components/FailureComponent";
import classes from "../../../summary/summary.module.scss";
import CostChartTooltip from "../../../summary/components/CostChartTooltip";
import DatabricksCostCategorySelector from "./DatabricksCostCategorySelector";
import DatabricksCostChartSkeleton from "./DatabricksCostChartSkeleton";
import DatabricksCostTotalsTile from "./DatabricksCostTotalsTile";
import DatabricksCostTotalsTileSkeleton from "./DatabricksCostTotalsTileSkeleton";
import { formatProductName } from "../../helpers";
import DatabricksCloudCostChart from "../../cloudCost/DatabricksCloudCostChart";
import { useCloudCostTrendsTotal } from "../../cloudCost/useCloudCost";
import {
  asTrendsGroup,
  CLOUD_COSTS_GROUP,
  COST_CATEGORY_OPTIONS,
  databricksColorFor,
  getGroupColor,
  getProductColor,
  SUMMARY_TIME_SERIES_CHART_PROPS,
  TRENDS_GROUPS,
} from "./costConstants";

interface Props {
  startDate: Date;
  endDate: Date | null;
}

const GRANULARITIES = ["day", "week", "month"] as const;
type Granularity = (typeof GRANULARITIES)[number];

const DatabricksCostPastView = ({ startDate, endDate }: Props) => {
  const { currency, showCloudCosts } = useAppState();
  const [granularity, setGranularity] = useSearchParam("granularity");
  const [selectedCategory, setSelectedCategory] = useSearchParam("pastCategory");
  const aggregateBy: Granularity = GRANULARITIES.find(value => value === granularity) ?? "day";
  const selectedGroup = asTrendsGroup(selectedCategory);
  // The Cloud Costs lens is gated behind `showCloudCosts`; when off, the
  // selector and rendering behave exactly as the DBU-only main view.
  const categoryOptions: readonly string[] = showCloudCosts ? COST_CATEGORY_OPTIONS : TRENDS_GROUPS;
  const isCloudCosts = showCloudCosts && selectedCategory === CLOUD_COSTS_GROUP;

  const startDateStr = dayjs(startDate).format("YYYY-MM-DD");
  const endDateStr = endDate ? dayjs(endDate).format("YYYY-MM-DD") : "";

  const costParams = { start_date: startDateStr, end_date: endDateStr };
  const {
    data: overviewData,
    isLoading: overviewLoading,
    error: overviewError,
  } = useDatabricksCostOverview(costParams);

  const {
    chartData,
    groups: chartGroups,
    productsByGroup,
    isLoading: trendsLoading,
    error: trendsError,
  } = useDatabricksPastTrends(startDateStr, endDateStr, aggregateBy, selectedGroup);

  // Hide toolbar (tabs + aggregate filter) until the first chart has rendered;
  // after that, keep it visible across tab switches so only the canvas swaps.
  const [hasRenderedChart, setHasRenderedChart] = useState(false);
  useEffect(() => {
    if (!trendsLoading) setHasRenderedChart(true);
  }, [trendsLoading]);

  // Cloud infra cost over the same window, surfaced as an extra totals tile
  // (only when the selected range actually has cloud cost). Shares the chart's
  // trends query, so no extra request.
  const { total: cloudCostTotal, isLoading: cloudCostLoading } = useCloudCostTrendsTotal(
    { ...costParams, granularity: aggregateBy },
    showCloudCosts && Boolean(endDateStr)
  );

  const groupCosts = useMemo(() => {
    const base = (overviewData?.category_breakdown ?? [])
      .filter(item => item.estimated_cost > 0)
      .map(item => ({ key: item.label, label: item.label, value: item.estimated_cost }));
    return showCloudCosts && cloudCostTotal > 0
      ? [...base, { key: CLOUD_COSTS_GROUP, label: CLOUD_COSTS_GROUP, value: cloudCostTotal }]
      : base;
  }, [overviewData, showCloudCosts, cloudCostTotal]);

  // Only a failing overview query blocks the whole section; a failing DBU trends
  // query is handled inline in the chart area so the tabs stay accessible (and
  // the Cloud Costs lens, which fetches its own data, is never blocked by it).
  const hasError = overviewError;

  return (
    <Card className={classes.section_card} id="tour-databricks-cost">
      <Stack align="center" gap="sm" className="al-mb-2">
        <Typography variant="subheading">Costs</Typography>
        <IconButton
          variant="ghost"
          title="The stacked bars show cost breakdown by category (Clusters, SQL Warehouse, AI/ML, Lakehouse, Platform). Each color represents a different category. Use the filter buttons to isolate specific categories. Hover over bars for exact values."
        >
          <IconInfoCircle size={16} />
        </IconButton>
      </Stack>
      <Stack>
        {hasError ? (
          <FailureComponent title="Failed to load cost breakdown. Please try again later." />
        ) : (
          <>
            {overviewLoading || cloudCostLoading ? (
              <DatabricksCostTotalsTileSkeleton />
            ) : (
              <DatabricksCostTotalsTile
                title="Total Costs"
                helpText={`Estimated Databricks costs from ${startDateStr} to ${endDateStr}`}
                total={overviewData?.estimated_cost ?? 0}
                breakdown={groupCosts}
              />
            )}
            <div className={classes.graphs}>
              {(hasRenderedChart || isCloudCosts) && (
                <Stack className="al-justify-between">
                  <DatabricksCostCategorySelector
                    options={categoryOptions}
                    selected={showCloudCosts ? selectedCategory : selectedGroup}
                    onSelect={setSelectedCategory}
                  />
                  <Stack className="al-items-center">
                    <AggregateFilter
                      onSelect={setGranularity}
                      aggregateBy={aggregateBy}
                      isFuture={false}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  </Stack>
                </Stack>
              )}
              {isCloudCosts ? (
                <DatabricksCloudCostChart
                  variant="past"
                  startDate={startDate}
                  endDate={endDate}
                  granularity={aggregateBy}
                />
              ) : trendsError ? (
                <FailureComponent title="Failed to load cost breakdown. Please try again later." />
              ) : trendsLoading ? (
                <DatabricksCostChartSkeleton />
              ) : (
                <div className="al-w-full al-h-[450px] al-p-4">
                  <Chart
                    {...SUMMARY_TIME_SERIES_CHART_PROPS}
                    key={selectedGroup || "total"}
                    title="Cost Trends by Category"
                    xAxisDataKey="period"
                    yAxisLabel={{ value: "Cost" }}
                    xAxisLabelFormatter={(value: string) =>
                      aggregateBy === "month"
                        ? dayjs(value).format("MMM YYYY")
                        : dayjs(value).format("MMM D")
                    }
                    yAxisLabelFormatter={(value: string) =>
                      formatNumber(Number(value), { currency, notation: "compact" })
                    }
                    type={ChartTypes.BarChart}
                    data={chartData}
                    tooltipProps={{
                      content: (props: TooltipProps<number, string>) => {
                        const fields = selectedGroup
                          ? [
                              {
                                key: { current: selectedGroup, estimate: "" },
                                label: `${selectedGroup} Total`,
                                isTotal: true,
                                hideSticker: true,
                                showChangePercentage: true,
                              },
                              ...(productsByGroup[selectedGroup] ?? []).map(product => ({
                                key: { current: `product_${product}`, estimate: "" },
                                label: formatProductName(product),
                                showChangePercentage: true,
                              })),
                            ]
                          : [
                              {
                                key: { current: "total", estimate: "" },
                                label: "Total Cost",
                                isTotal: true,
                                hideSticker: true,
                                showChangePercentage: true,
                              },
                              ...chartGroups.map(group => ({
                                key: { current: group, estimate: "" },
                                label: group,
                                showChangePercentage: true,
                              })),
                            ];
                        return (
                          <CostChartTooltip
                            {...props}
                            timeRange={aggregateBy}
                            dateKey="period"
                            fields={fields}
                            colorFor={databricksColorFor}
                          />
                        );
                      },
                    }}
                  >
                    {({ barSize, selectedLegend }) => {
                      if (selectedGroup) {
                        const products = productsByGroup[selectedGroup] || [];
                        return (
                          <>
                            {products.map((product, index) => (
                              <Bar
                                key={product}
                                barSize={barSize}
                                name={formatProductName(product)}
                                dataKey={`product_${product}`}
                                stackId="cost"
                                fill={getProductColor(product, selectedGroup)}
                                hide={Boolean(
                                  selectedLegend && selectedLegend !== `product_${product}`
                                )}
                                radius={index === products.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                              />
                            ))}
                          </>
                        );
                      }
                      return (
                        <>
                          {chartGroups.map((group, index) => (
                            <Bar
                              key={group}
                              barSize={barSize}
                              name={group}
                              dataKey={group}
                              stackId="cost"
                              fill={getGroupColor(group)}
                              hide={Boolean(selectedLegend && selectedLegend !== group)}
                              radius={
                                index === chartGroups.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]
                              }
                            />
                          ))}
                        </>
                      );
                    }}
                  </Chart>
                </div>
              )}
            </div>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default DatabricksCostPastView;
